import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Settings, FileText, ChevronRight } from 'lucide-react';
import FormularioVersoes from './FormularioVersoes';
import FormularioQuestionario from './FormularioQuestionario';

// =======================================================================
// Preenchimento VERSIONADO da Avaliação de Treinamento (TSV) — DEM-00014.
// -----------------------------------------------------------------------
// Mini-hub embutido no card "Avaliação de Treinamento" (TreinamentoForms):
//   'lista'    → escolhe a versão publicada (+ "Gerenciar versões", se gestão)
//   'preencher'→ questionário daquela versão (FormularioQuestionario)
//   'versoes'  → construtor de versões (FormularioVersoes)
// A TSV compartilha o backend com a SA pela factory: mesma tela, só muda a base
// da API ('/api/avaliacao') e a cor do módulo Treinamento (#0284c7).
// =======================================================================

const API = '/api/avaliacao';
const API_VERSOES = '/api/avaliacao/versoes';
const COR = '#0284c7';
const PERFIS_GESTAO = ['Admin', 'Treinamento'];

const AvaliacaoTSVPreencher = () => {
  const [modo, setModo] = useState('lista'); // 'lista' | 'preencher' | 'versoes'
  const [versaoEscolhida, setVersaoEscolhida] = useState(null);
  const [versoes, setVersoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const podeGerenciar = PERFIS_GESTAO.includes(user.perfil);

  const carregar = useCallback(() => {
    setCarregando(true); setErro('');
    fetch(`${API}/versoes-publicadas`, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Erro ao carregar versões.'))))
      .then((data) => setVersoes(Array.isArray(data) ? data : []))
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { if (modo === 'lista') carregar(); }, [modo, carregar]);

  if (modo === 'versoes') {
    return (
      <FormularioVersoes
        apiBase={API_VERSOES}
        cor={COR}
        titulo="Gerenciar Versões — Avaliação de Treinamento"
        onVoltar={() => setModo('lista')}
      />
    );
  }

  if (modo === 'preencher') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <button onClick={() => setModo('lista')} style={btnVoltar}>
          <ArrowLeft size={16} /> Voltar às versões
        </button>
        <FormularioQuestionario
          apiBase={API}
          cor={COR}
          idVersao={versaoEscolhida}
          titulo="Avaliação de Treinamento"
          onConcluido={() => setModo('lista')}
        />
      </div>
    );
  }

  // modo 'lista'
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Nova Avaliação de Treinamento</h3>
        {podeGerenciar && (
          <button onClick={() => setModo('versoes')} style={btnSecundario} title="Criar/editar versões do formulário">
            <Settings size={16} /> Gerenciar versões
          </button>
        )}
      </div>

      {carregando && <p style={{ color: '#64748b' }}>Carregando versões…</p>}
      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>{erro}</div>}
      {!carregando && !erro && versoes.length === 0 && (
        <div style={{ padding: 14, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, fontSize: 14 }}>
          Nenhuma versão publicada ainda.{podeGerenciar ? <> Use <strong>“Gerenciar versões”</strong> para criar e publicar a primeira.</> : ' Peça ao Treinamento para publicar uma versão.'}
        </div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {versoes.map((v) => (
          <div
            key={v.ID}
            onClick={() => { setVersaoEscolhida(v.ID); setModo('preencher'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 16, padding: 18, background: '#fff',
              border: '1px solid #e2e8f0', borderRadius: 12, cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}>
            <div style={{ padding: 12, background: '#e0f2fe', borderRadius: 10 }}><FileText size={22} color={COR} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</div>
              <div style={{ marginTop: 4, color: '#64748b', fontSize: 14 }}>
                {v.DESCRICAO ? `${v.DESCRICAO} · ` : ''}{v.TOTAL_PERGUNTAS} perguntas ·{' '}
                {v.CORTE_VETO === null || v.CORTE_VETO === undefined ? 'sem corte' : `corte ${Number(v.CORTE_VETO)}%`}
              </div>
            </div>
            <ChevronRight size={20} color="#94a3b8" />
          </div>
        ))}
      </div>
    </div>
  );
};

const btnVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
  padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};
const btnSecundario = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: '#fff', color: COR, border: `1px solid ${COR}`,
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

export default AvaliacaoTSVPreencher;
